import { BrowserRouter, Navigate, Route, Routes, useParams } from 'react-router-dom';

import Layout from './components/Layout';
import PermissionRoute from './components/PermissionRoute';
import {
  DepartmentsTabGuard,
  PlatformConfigMasterGuard,
  SlaTabGuard,
} from './components/PlatformConfigTabGuard';
import ProtectedRoute from './components/ProtectedRoute';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { SocketProvider } from './contexts/SocketContext';
import { ToastProvider } from './contexts/ToastContext';
import { ThemeProvider } from './contexts/ThemeContext';
import EntitySourceFormPage from './pages/entity-sources/EntitySourceFormPage';
import EntitySourcesPage from './pages/entity-sources/EntitySourcesPage';
import IntakeRouteFormPage from './pages/intake-routes/IntakeRouteFormPage';
import IntakeRoutesPage from './pages/intake-routes/IntakeRoutesPage';
import ChannelsPage from './pages/channels/ChannelsPage';
import ChannelFormPage from './pages/channels/ChannelFormPage';
import ChannelTypeFormPage from './pages/channel-types/ChannelTypeFormPage';
import ChannelTypesPage from './pages/channel-types/ChannelTypesPage';
import DashboardPage from './pages/dashboard/DashboardPage';
import DepartmentFormPage from './pages/departments/DepartmentFormPage';
import DepartmentsPage from './pages/departments/DepartmentsPage';
import LoginPage from './pages/login/LoginPage';
import NotificationsPage from './pages/notifications/NotificationsPage';
import PlatformConfigLayout, {
  PlatformConfigHomeRedirect,
} from './pages/platform-config/PlatformConfigLayout';
import { PLATFORM_CONFIG } from './pages/platform-config/paths';
import RoleFormPage from './pages/roles/RoleFormPage';
import RolesPage from './pages/roles/RolesPage';
import SlaRuleFormPage from './pages/sla/SlaRuleFormPage';
import SlaRulesPage from './pages/sla/SlaRulesPage';
import TicketCategoryFormPage from './pages/ticket-categories/TicketCategoryFormPage';
import TicketCategoriesPage from './pages/ticket-categories/TicketCategoriesPage';
import TicketLabelFormPage from './pages/ticket-labels/TicketLabelFormPage';
import TicketLabelsPage from './pages/ticket-labels/TicketLabelsPage';
import TicketStatusFormPage from './pages/ticket-statuses/TicketStatusFormPage';
import TicketStatusesPage from './pages/ticket-statuses/TicketStatusesPage';
import CreateTicketPage from './pages/tickets/CreateTicketPage';
import TicketDetailPage from './pages/TicketDetail';
import TicketsSplitLayout from './pages/tickets/TicketsSplitLayout';
import ReportsPage from './pages/Reports/ReportsPage';
import TeamFormPage from './pages/teams/TeamFormPage';
import TeamsPage from './pages/teams/TeamsPage';
import UserFormPage from './pages/users/UserFormPage';
import UsersPage from './pages/users/UsersPage';
import {
  canAccessDepartments,
  canAccessPlatformConfig,
  canAccessPlatformConfigSection,
  canAccessRoleManagement,
  canAccessStaffManagement,
  canEditDepartments,
  canEditPlatformConfig,
  canEditRoles,
  canEditSla,
  canEditStaff,
  canViewSla,
  canViewReports,
  canWriteDepartments,
  canWritePlatformConfig,
  canWriteRoles,
  canWriteSla,
  canWriteStaff,
} from './utils/permissions';

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <AuthenticatedSocketProvider>
        <ToastProvider>
        <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route element={<ProtectedRoute />}>
            <Route element={<Layout />}>
              <Route index element={<DashboardPage />} />
              <Route path="tickets/new" element={<CreateTicketPage />} />
              <Route path="tickets" element={<TicketsSplitLayout />}>
                <Route path=":id" element={<TicketDetailPage embedded />} />
              </Route>
              <Route path="notifications" element={<NotificationsPage />} />
              <Route element={<PermissionRoute check={canViewReports} />}>
                <Route path="reports" element={<ReportsPage />} />
              </Route>

              <Route path="platform-config">
                <Route element={<PermissionRoute check={canAccessPlatformConfigSection} />}>
                  <Route element={<PlatformConfigLayout />}>
                    <Route index element={<PlatformConfigHomeRedirect />} />
                    <Route
                      path="channel-types"
                      element={
                        <PlatformConfigMasterGuard>
                          <ChannelTypesPage />
                        </PlatformConfigMasterGuard>
                      }
                    />
                    <Route
                      path="labels"
                      element={
                        <PlatformConfigMasterGuard>
                          <TicketLabelsPage />
                        </PlatformConfigMasterGuard>
                      }
                    />
                    <Route
                      path="categories"
                      element={
                        <PlatformConfigMasterGuard>
                          <TicketCategoriesPage />
                        </PlatformConfigMasterGuard>
                      }
                    />
                    <Route
                      path="statuses"
                      element={
                        <PlatformConfigMasterGuard>
                          <TicketStatusesPage />
                        </PlatformConfigMasterGuard>
                      }
                    />
                    <Route
                      path="channels"
                      element={
                        <PlatformConfigMasterGuard>
                          <ChannelsPage />
                        </PlatformConfigMasterGuard>
                      }
                    />
                    <Route
                      path="sources"
                      element={
                        <PlatformConfigMasterGuard>
                          <EntitySourcesPage />
                        </PlatformConfigMasterGuard>
                      }
                    />
                    <Route
                      path="routing"
                      element={
                        <PlatformConfigMasterGuard>
                          <IntakeRoutesPage />
                        </PlatformConfigMasterGuard>
                      }
                    />
                    <Route
                      path="departments"
                      element={
                        <DepartmentsTabGuard>
                          <DepartmentsPage />
                        </DepartmentsTabGuard>
                      }
                    />
                    <Route
                      path="sla"
                      element={
                        <SlaTabGuard>
                          <SlaRulesPage />
                        </SlaTabGuard>
                      }
                    />
                  </Route>
                </Route>
                <Route element={<PermissionRoute check={canWritePlatformConfig} />}>
                  <Route path="channel-types/new" element={<ChannelTypeFormPage />} />
                  <Route path="labels/new" element={<TicketLabelFormPage />} />
                  <Route path="categories/new" element={<TicketCategoryFormPage />} />
                  <Route path="statuses/new" element={<TicketStatusFormPage />} />
                  <Route path="channels/new" element={<ChannelFormPage />} />
                  <Route path="sources/new" element={<EntitySourceFormPage />} />
                  <Route path="routing/new" element={<IntakeRouteFormPage />} />
                </Route>
                <Route element={<PermissionRoute check={canEditPlatformConfig} />}>
                  <Route path="channel-types/:id" element={<ChannelTypeFormPage />} />
                  <Route path="labels/:id" element={<TicketLabelFormPage />} />
                  <Route path="categories/:id" element={<TicketCategoryFormPage />} />
                  <Route path="statuses/:id" element={<TicketStatusFormPage />} />
                  <Route path="channels/:id" element={<ChannelFormPage />} />
                  <Route path="sources/:id" element={<EntitySourceFormPage />} />
                  <Route path="routing/:id" element={<IntakeRouteFormPage />} />
                </Route>
                <Route element={<PermissionRoute check={canEditDepartments} />}>
                  <Route path="departments/:id" element={<DepartmentFormPage />} />
                  <Route path="departments/:deptId/teams" element={<TeamsPage />} />
                  <Route path="departments/:deptId/teams/:id" element={<TeamFormPage />} />
                </Route>
                <Route element={<PermissionRoute check={canWriteDepartments} />}>
                  <Route path="departments/new" element={<DepartmentFormPage />} />
                  <Route path="departments/:deptId/teams/new" element={<TeamFormPage />} />
                </Route>
                <Route element={<PermissionRoute check={canWriteSla} />}>
                  <Route path="sla/new" element={<SlaRuleFormPage />} />
                </Route>
                <Route element={<PermissionRoute check={canEditSla} />}>
                  <Route path="sla/:id" element={<SlaRuleFormPage />} />
                </Route>
              </Route>

              {/* Legacy paths → platform config */}
              <Route path="channels" element={<Navigate to={PLATFORM_CONFIG.channels} replace />} />
              <Route path="channels/new" element={<Navigate to={PLATFORM_CONFIG.channelsNew} replace />} />
              <Route path="channels/:id" element={<LegacyChannelRedirect />} />
              <Route path="channel-types" element={<Navigate to={PLATFORM_CONFIG.channelTypes} replace />} />
              <Route path="channel-types/new" element={<Navigate to={PLATFORM_CONFIG.channelTypesNew} replace />} />
              <Route path="channel-types/:id" element={<LegacyChannelTypeRedirect />} />
              <Route path="ticket-labels" element={<Navigate to={PLATFORM_CONFIG.labels} replace />} />
              <Route path="ticket-labels/new" element={<Navigate to={PLATFORM_CONFIG.labelsNew} replace />} />
              <Route path="ticket-labels/:id" element={<LegacyLabelRedirect />} />
              <Route path="ticket-categories" element={<Navigate to={PLATFORM_CONFIG.categories} replace />} />
              <Route path="ticket-categories/new" element={<Navigate to={PLATFORM_CONFIG.categoriesNew} replace />} />
              <Route path="ticket-categories/:id" element={<LegacyCategoryRedirect />} />
              <Route path="ticket-statuses" element={<Navigate to={PLATFORM_CONFIG.statuses} replace />} />
              <Route path="ticket-statuses/new" element={<Navigate to={PLATFORM_CONFIG.statusesNew} replace />} />
              <Route path="ticket-statuses/:id" element={<LegacyStatusRedirect />} />
              <Route path="departments" element={<Navigate to={PLATFORM_CONFIG.departments} replace />} />
              <Route path="departments/new" element={<Navigate to={PLATFORM_CONFIG.departmentsNew} replace />} />
              <Route path="departments/:id" element={<LegacyDepartmentRedirect />} />
              <Route path="sla" element={<Navigate to={PLATFORM_CONFIG.sla} replace />} />
              <Route path="sla/new" element={<Navigate to={PLATFORM_CONFIG.slaNew} replace />} />
              <Route path="sla/:id" element={<LegacySlaRedirect />} />

              <Route element={<PermissionRoute check={canAccessRoleManagement} />}>
                <Route path="roles" element={<RolesPage />} />
              </Route>
              <Route
                element={
                  <PermissionRoute check={(u) => canWriteRoles(u) || canEditRoles(u)} />
                }
              >
                <Route path="roles/new" element={<RoleFormPage />} />
                <Route path="roles/:id" element={<RoleFormPage />} />
              </Route>

              <Route element={<PermissionRoute check={canAccessStaffManagement} />}>
                <Route path="users" element={<UsersPage />} />
              </Route>
              <Route element={<PermissionRoute check={canWriteStaff} />}>
                <Route path="users/new" element={<UserFormPage />} />
              </Route>
              <Route element={<PermissionRoute check={canEditStaff} />}>
                <Route path="users/:id" element={<UserFormPage />} />
              </Route>
            </Route>
          </Route>
          <Route path="*" element={<Navigate to="/tickets" replace />} />
        </Routes>
        </BrowserRouter>
        </ToastProvider>
        </AuthenticatedSocketProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

function AuthenticatedSocketProvider({ children }: { children: React.ReactNode }) {
  const { token } = useAuth();
  return (
    <SocketProvider token={token}>
      <NotificationProvider>{children}</NotificationProvider>
    </SocketProvider>
  );
}

function LegacyChannelRedirect() {
  const { id } = useParams();
  return <Navigate to={PLATFORM_CONFIG.channel(id ?? '')} replace />;
}

function LegacyChannelTypeRedirect() {
  const { id } = useParams();
  return <Navigate to={PLATFORM_CONFIG.channelType(id ?? '')} replace />;
}

function LegacyLabelRedirect() {
  const { id } = useParams();
  return <Navigate to={PLATFORM_CONFIG.label(id ?? '')} replace />;
}

function LegacyCategoryRedirect() {
  const { id } = useParams();
  return <Navigate to={PLATFORM_CONFIG.category(id ?? '')} replace />;
}

function LegacyStatusRedirect() {
  const { id } = useParams();
  return <Navigate to={PLATFORM_CONFIG.status(id ?? '')} replace />;
}

function LegacyDepartmentRedirect() {
  const { id } = useParams();
  return <Navigate to={PLATFORM_CONFIG.department(id ?? '')} replace />;
}

function LegacySlaRedirect() {
  const { id } = useParams();
  return <Navigate to={PLATFORM_CONFIG.slaRule(id ?? '')} replace />;
}
